import { AudioManager } from '../audio/audio-manager';
import { createLeaderboardClient, type LeaderboardClient } from '../net/leaderboard-client';
import {
  addHighscore,
  hasMigrated,
  loadHighscores,
  markMigrated,
  normaliseInitials,
  qualifies,
  saveHighscores,
  type HighscoreEntry,
} from './highscores';
import { mergeEntries, type LeaderboardEntry } from './leaderboard';
import {
  createPlayerSelectState,
  type GameMode,
  type PlayerSelectState,
} from './player-select';
import { createRaceState, scoreFor, type RaceState } from './race-state';
import { loadSettings, saveSettings, type GameSettings } from './settings';

/**
 * State that outlives any single scene: which mode is being played, who picked
 * what, and the audio/settings singletons.
 *
 * Scenes read this from the Phaser registry under {@link SESSION_KEY}, which
 * keeps them constructible in tests with a hand-built session.
 */
export class GameSession {
  readonly audio: AudioManager;
  readonly leaderboard: LeaderboardClient;
  private settings: GameSettings;
  private selectState: PlayerSelectState;

  constructor(
    settings: GameSettings = loadSettings(),
    audio?: AudioManager,
    leaderboard: LeaderboardClient = createLeaderboardClient(),
  ) {
    this.settings = settings;
    this.audio = audio ?? new AudioManager(settings);
    this.leaderboard = leaderboard;
    this.selectState = createPlayerSelectState('highscore_race');
  }

  getSettings(): GameSettings {
    return this.settings;
  }

  updateSettings(patch: Partial<GameSettings>): GameSettings {
    this.settings = { ...this.settings, ...patch };
    this.audio.applySettings(this.settings);
    saveSettings(this.settings);
    return this.settings;
  }

  /** Resets the select screen. Called every time a mode is chosen. */
  startMode(mode: GameMode): PlayerSelectState {
    this.selectState = createPlayerSelectState(mode);
    return this.selectState;
  }

  getSelectState(): PlayerSelectState {
    return this.selectState;
  }

  setSelectState(state: PlayerSelectState): void {
    this.selectState = state;
  }

  /**
   * Builds the race from whatever was chosen on the select screen.
   *
   * The seed is captured on the state itself, so a run can be replayed — and,
   * once matches go online, shared with an opponent instead of streaming every
   * rock across the wire.
   */
  startRace(seed?: number): RaceState {
    const { mode, selections } = this.selectState;
    return seed === undefined
      ? createRaceState(mode, selections)
      : createRaceState(mode, selections, seed);
  }

  /**
   * Whether a finished run earned a place on the board, and is therefore worth
   * asking for initials. Both modes count — beating the board with a friend
   * watching is the whole point.
   */
  qualifiesForBoard(state: RaceState): boolean {
    const best = bestRacerScore(state);
    return best > 0 && qualifies(loadHighscores(), best);
  }

  /** Records a finished run under the given initials. */
  recordResult(state: RaceState, initials: string): HighscoreEntry[] {
    const racer = [...state.racers].sort((a, b) => scoreFor(b) - scoreFor(a))[0];
    if (!racer || scoreFor(racer) <= 0) {
      return this.getHighscores();
    }

    const entry: HighscoreEntry = {
      initials: normaliseInitials(initials),
      character: racer.character,
      score: scoreFor(racer),
      survivedMs: racer.survivedMs,
      recordedAt: Date.now(),
    };
    const updated = addHighscore(loadHighscores(), entry);
    saveHighscores(updated);

    // Deliberately not awaited. The run is already saved locally, so a slow or
    // failed submission must not hold up the results screen or lose the score.
    void this.leaderboard.submit({
      initials: entry.initials,
      character: entry.character,
      score: entry.score,
      survivedMs: entry.survivedMs,
      seed: state.field.seed,
    });

    return updated;
  }

  getHighscores(): HighscoreEntry[] {
    return loadHighscores();
  }

  /**
   * The board to show the player.
   *
   * The global board is the source of truth and is shown alone when it loads —
   * merging the local cache into it would double up any run that appears in
   * both under two different timestamps. The cache is what you get instead when
   * the board is unreachable, and `global` says which one you are looking at.
   */
  async loadGlobalBoard(): Promise<{ entries: LeaderboardEntry[]; global: boolean }> {
    const remote = await this.leaderboard.fetchBoard();
    if (remote !== null) {
      return { entries: remote, global: true };
    }
    return { entries: mergeEntries([], loadHighscores()), global: false };
  }

  /**
   * Carries pre-existing local scores up to the global board, once.
   *
   * Boards were browser-local before the leaderboard existed, so without this
   * every score anybody had already set would simply stop counting.
   */
  async syncLocalScoresOnce(): Promise<void> {
    if (!this.leaderboard.configured || hasMigrated()) {
      return;
    }
    const local = loadHighscores();
    if (local.length === 0) {
      markMigrated();
      return;
    }

    let pushed = 0;
    for (const entry of local) {
      const accepted = await this.leaderboard.submit({
        initials: entry.initials,
        character: entry.character,
        score: entry.score,
        survivedMs: entry.survivedMs,
        // These runs predate the board recording a seed, so there is none to
        // give. Zero marks them as unverifiable rather than pretending.
        seed: 0,
      });
      if (accepted !== null) {
        pushed += 1;
      }
    }

    // Marked as soon as anything landed, so a retry can never post the same run
    // twice. If nothing landed the flag stays clear and the next visit retries.
    if (pushed > 0) {
      markMigrated();
    }
  }
}

/** The best score posted in a finished race, across both racers. */
function bestRacerScore(state: RaceState): number {
  return state.racers.reduce((best, racer) => Math.max(best, scoreFor(racer)), 0);
}

export const SESSION_KEY = 'session';
